import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { securedAxiosInstance } from '../../../api/axios';
import {
  assumeEmployeeRole,
  getEmployees,
  unlinkEmployees,
} from '../../../stores/slices/employees/employeesActions';
import { filterByValue } from '../../../utils/filterByValue';
import DeleteAlert from '../../Alerts/DeleteAlert';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';

const schema = yup
  .object({
    inviteEmail: yup
      .string()
      .email('Must be a valid email')
      .required('Email is required'),
  })
  .required();
const Employees = () => {
  const { employees, isLoading } = useSelector(state => state.employees);
  const { userInfo } = useSelector(state => state.user);
  const [isAssumming, setIsAssumming] = useState(false);
  const [selectedEmployeeToAssume, setSelectedEmployeeToAssume] =
    useState(null);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [managers, setManagers] = useState([]);
  const dispatch = useDispatch();
  const searchRef = useRef();
  const inviteRef = useRef();
  const cancelRef = useRef();
  const {
    isOpen: isOpenAlert,
    onOpen: onOpenAlertAlert,
    onClose: onCloseAlert,
  } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  });
  useEffect(() => {
    dispatch(getEmployees());
    updateManagerList();
  }, []);

  useEffect(() => {
    setFilteredEmployees(employees);
  }, [employees]);

  const handleSelectAll = event => {
    let newSelectedEmployees;

    if (event.target.checked) {
      newSelectedEmployees = employees
        // .filter(emp => userInfo.id !== emp.id)
        .map(employee => employee.id);
    } else {
      newSelectedEmployees = [];
    }

    setSelectedEmployees(newSelectedEmployees);
  };
  const handleSelectOne = (event, id) => {
    const selectedIndex = selectedEmployees.indexOf(id);
    let newSelectedEmployees = [];

    if (selectedIndex === -1) {
      newSelectedEmployees = newSelectedEmployees.concat(selectedEmployees, id);
    } else if (selectedIndex === 0) {
      newSelectedEmployees = newSelectedEmployees.concat(
        selectedEmployees.slice(1)
      );
    } else if (selectedIndex === selectedEmployees.length - 1) {
      newSelectedEmployees = newSelectedEmployees.concat(
        selectedEmployees.slice(0, -1)
      );
    } else if (selectedIndex > 0) {
      newSelectedEmployees = newSelectedEmployees.concat(
        selectedEmployees.slice(0, selectedIndex),
        selectedEmployees.slice(selectedIndex + 1)
      );
    }

    setSelectedEmployees(newSelectedEmployees);
  };
  const filter = data => {
    const text = data.target.value;
    setFilteredEmployees(filterByValue(employees, text));
  };

  const clearInput = () => {
    searchRef.current.value = '';
    setFilteredEmployees(employees);
  };

  const onUnlinkEmployees = async () => {
    setIsDeleting(true);
    const res = await dispatch(unlinkEmployees({ users: selectedEmployees }));
    await dispatch(getEmployees());
    onCloseAlert();
    setIsDeleting(false);
    if (res.type === 'employees/unlinkEmployees/rejected') {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    } else {
      setSelectedEmployees([]);
      toast({
        description: 'Users has been unlinked',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    }
  };

  const inviteEmployee = async data => {
    const res = await securedAxiosInstance.post('/invite', {
      user: { email: data.inviteEmail },
    });

    if (res.status >= 200 && res.status < 400) {
      reset();
      toast({
        description: 'User has been invited',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    } else {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    }
  };

  const assumeRole = async id => {
    setIsAssumming(true);
    const res = await dispatch(assumeEmployeeRole({ id }));
    if (res.type === 'employees/assumeEmployeeRole/rejected') {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    } else {
      navigate('/dashboard');
      toast({
        description: 'User assumed',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    }
    setIsAssumming(false);
    setSelectedEmployeeToAssume(null);
  };

  const toggleManager = async (id, event) => {
    setFilteredEmployees(employees => {
      return employees.map(employee => {
        if (employee.id === id) {
          return {
            ...employee,
            role: event.target.checked ? 'manager' : 'employee',
          };
        }
        return employee;
      });
    });

    try {
      const response = await securedAxiosInstance.post(
        '/organisations/toggleManagers',
        {
          manager: id,
          enabled: event.target.checked,
        }
      );
      setManagers(response.data);
    } catch (error) {
      updateManagerList();
    }
  };

  const updateManagerList = async () => {
    try {
      const response = await securedAxiosInstance.get(
        '/organisations/managers'
      );
      setManagers(response.data);
    } catch (error) {}
  };

  const setManager = async (id, event) => {
    securedAxiosInstance.post('/organisations/setManager', {
      user: id,
      manager: event.target.value,
    });
  };
  return (
    <React.Fragment>
      <Box w={'full'} overflowX={'hidden'} overflowY={'auto'}>
        <VStack spacing={'4'}>
          <Stack
            justifyContent={'space-between'}
            alignContent={'center'}
            direction={{ base: 'column', sm: 'row' }}
            w={'full'}
            spacing={4}
            pr={'2'}
            pb={'2'}
            zIndex={'10'}
            sx={{
              position: '-webkit-sticky',
              /* Safari */
              position: 'sticky',
              top: '0',
            }}
            bg={'white'}
            borderBottom={'1px'}
            borderColor={'gray.500'}
          >
            <InputGroup size="sm" maxW={{ base: '100%', md: '40%' }}>
              <Input
                pr="4.5rem"
                placeholder="Search"
                borderRadius={'4'}
                onChange={filter}
                ref={searchRef}
              />
              <InputRightElement width="4.5rem" justifyContent={'end'}>
                <Button
                  size={'sm'}
                  borderLeftRadius={'none'}
                  onClick={clearInput}
                >
                  Clear
                </Button>
              </InputRightElement>
            </InputGroup>
            <HStack
              spacing={2}
              w={'full'}
              maxW={{ base: '100%', md: '40%' }}
              align={'start'}
            >
              <Box w={'full'}>
                <form onSubmit={handleSubmit(inviteEmployee)}>
                  <FormControl id="inviteEmail" isInvalid={errors.inviteEmail}>
                    <InputGroup size="sm">
                      <Input
                        pr="4.5rem"
                        placeholder="Invite Employees by email"
                        borderRadius={'4'}
                        ref={inviteRef}
                        id="inviteEmail"
                        {...register('inviteEmail')}
                      />
                      <InputRightElement width="4.5rem" justifyContent={'end'}>
                        <Button
                          size={'sm'}
                          borderLeftRadius={'none'}
                          isLoading={isSubmitting}
                          type="submit"
                        >
                          Invite
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                    <FormErrorMessage>
                      {errors.inviteEmail && errors.inviteEmail.message}
                    </FormErrorMessage>
                  </FormControl>
                </form>
              </Box>
              <Button
                size={'sm'}
                disabled={selectedEmployees.length <= 0}
                bg={'red.400'}
                _hover={{
                  color: 'red.400',
                  bg: 'transparent',
                }}
                onClick={onOpenAlertAlert}
              >
                Unlink
              </Button>
            </HStack>
          </Stack>
          <Box overflowX={'auto'} w={'full'}>
            <TableContainer>
              <Table
                variant="striped"
                colorScheme="gray"
                display={{
                  base: 'block',
                  md: 'table',
                }}
              >
                <Thead>
                  <Tr>
                    <Th>
                      <Checkbox
                        value="true"
                        onChange={handleSelectAll}
                        isChecked={
                          selectedEmployees.length === employees.length
                        }
                      />
                    </Th>
                    <Th>Employee</Th>
                    <Th>Email</Th>
                    {userInfo.role === 'employer' ? <Th>Is Manager?</Th> : null}
                    {userInfo.role === 'employer' ? <Th>Managers</Th> : null}
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {employees &&
                    (isLoading ? (
                      <Tr>
                        <Td colSpan={userInfo.role === 'employer' ? '6' : '4'}>
                          <Spinner color="primary" />
                        </Td>
                      </Tr>
                    ) : employees.length > 0 ? (
                      filteredEmployees.map(employee => (
                        <Tr key={employee.id}>
                          <Td>
                            <Checkbox
                              value="true"
                              disabled={userInfo.id === employee.id}
                              isChecked={
                                selectedEmployees.indexOf(employee.id) !== -1
                              }
                              onChange={event =>
                                handleSelectOne(event, employee.id)
                              }
                            />
                          </Td>
                          <Td>{employee.full_name}</Td>
                          <Td>{employee.email}</Td>
                          {userInfo.role === 'employer' ? (
                            <Td>
                              <Checkbox
                                value="true"
                                isDisabled={
                                  userInfo.id === employee.id ||
                                  employee.role === 'employer'
                                }
                                isChecked={
                                  employee.role == 'manager' ||
                                  employee.role == 'employer'
                                }
                                onChange={event =>
                                  toggleManager(employee.id, event)
                                }
                              />
                            </Td>
                          ) : null}
                          {userInfo.role === 'employer' ? (
                            <Td>
                              <Select
                                id="manager"
                                defaultValue={employee.parent_account}
                                isDisabled={employee.role === 'employer'}
                                onChange={event =>
                                  setManager(employee.id, event)
                                }
                              >
                                {managers && managers.length > 0 ? (
                                  managers.map(manager => (
                                    <option key={manager.id} value={manager.id}>
                                      {manager.full_name}
                                    </option>
                                  ))
                                ) : (
                                  <option value={''}>No manager found</option>
                                )}
                              </Select>
                            </Td>
                          ) : null}
                          <Td align={'right'} textAlign={'right'}>
                            <HStack justify={'end'}>
                              <Button
                                isLoading={
                                  isAssumming &&
                                  selectedEmployeeToAssume === employee.id
                                }
                                onClick={() => {
                                  setSelectedEmployeeToAssume(employee.id);
                                  assumeRole(employee.id);
                                }}
                              >
                                Assume Role
                              </Button>
                            </HStack>
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={userInfo.role === 'employer' ? '6' : '4'}>
                          No employees found
                        </Td>
                      </Tr>
                    ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        </VStack>
      </Box>
      <DeleteAlert
        isOpen={isOpenAlert}
        cancelRef={cancelRef}
        onClose={onCloseAlert}
        isDeleting={isDeleting}
        header={`Unlink Employee${selectedEmployees.length > 1 ? 's' : ''}`}
        body={
          "If you unlink this user, it'll remove them from your account completely."
        }
        onDeleteItem={onUnlinkEmployees}
      />
    </React.Fragment>
  );
};

export default Employees;
